import React, { useState } from "react";
import { Modal, Button, Row, Col, Card, ListGroup, Spinner, Tabs, Tab, Alert, Table, Form, Badge } from "react-bootstrap";
import { CheckCircle, XCircle } from "react-bootstrap-icons";
import { formatCurrency } from "../../../store/config";
import Swal from "sweetalert2";

function LandAcquisitionDetailsModal({
    show,
    onHide,
    request,
    processingAction,
    handleUpdateStatus,
    StatusBadge
}) {
    const [rejectionReason, setRejectionReason] = useState("");

    const isPending = request?.status === "pending";
    const isApproved = request?.status === "approved";
    const isMember = request?.memberType === "member";

    const handleApprove = () => {
        Swal.fire({
            title: 'Approve Request',
            text: 'Are you sure you want to approve this land acquisition request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, approve it!',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                handleUpdateStatus("approved");
            }
        });
    };

    const handleReject = () => {
        if (!rejectionReason.trim()) {
            Swal.fire({
                title: 'Error!',
                text: 'Please provide a reason for rejection',
                icon: 'error',
                confirmButtonColor: '#dc3545'
            });
            return;
        }

        Swal.fire({
            title: 'Reject Request',
            text: 'Are you sure you want to reject this land acquisition request?',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reject it!',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                handleUpdateStatus("rejected", rejectionReason);
            }
        });
    };

    const handleComplete = () => {
        Swal.fire({
            title: 'Complete Request',
            text: 'Are you sure you want to mark this request as completed?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#007bff',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, complete it!',
            cancelButtonText: 'Cancel'
        }).then((result) => {
            if (result.isConfirmed) {
                handleUpdateStatus("completed");
            }
        });
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            backdrop="static"
            keyboard={!processingAction}
        >
            <Modal.Header closeButton={!processingAction}>
                <Modal.Title>
                    Land Acquisition Request Details
                    {request && (
                        <>
                            <StatusBadge status={request.status} className="ml-3" />
                            <Badge
                                variant={request.memberType === 'member' ? 'primary' : 'secondary'}
                                className="ml-2"
                            >
                                {request.memberType === 'member' ? 'Member' : 'Non-Member'}
                            </Badge>
                        </>
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {request && (
                    <>
                        <div className="mb-4">
                            <h6 className="border-bottom pb-2">Request Information</h6>
                            <Row>
                                <Col md={6}>
                                    <small className="text-muted d-block">Request Date</small>
                                    <div>{new Date(request.createdAt || request.requestDate).toLocaleDateString()}</div>
                                </Col>
                                <Col md={6}>
                                    <small className="text-muted d-block">Status</small>
                                    <StatusBadge status={request.status} />
                                </Col>
                                {request.approvalDate && (
                                    <Col md={6} className="mt-3">
                                        <small className="text-muted d-block">Approval Date</small>
                                        <div>{new Date(request.approvalDate).toLocaleDateString()}</div>
                                    </Col>
                                )}
                                {request.approvedBy && (
                                    <Col md={6} className="mt-3">
                                        <small className="text-muted d-block">Approved By</small>
                                        <div>{request.approvedBy}</div>
                                    </Col>
                                )}
                                {request.rejectionReason && (
                                    <Col xs={12} className="mt-3">
                                        <Alert variant="danger">
                                            <strong>Rejection Reason:</strong> {request.rejectionReason}
                                        </Alert>
                                    </Col>
                                )}
                            </Row>
                        </div>

                        <Tabs defaultActiveKey="member" className="mb-4">
                            <Tab eventKey="member" title="Personal Information">
                                <Card className="border-0 mt-3">
                                    <Card.Body>
                                        <Row>
                                            {isMember && (
                                                <Col md={6} className="mb-2">
                                                    <small className="text-muted d-block">Member ID</small>
                                                    <div>{request.memberId}</div>
                                                </Col>
                                            )}
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Full Name</small>
                                                <div>{request.personalInfo?.fullName || "N/A"}</div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Email</small>
                                                <div>{request.personalInfo?.email || "N/A"}</div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Phone Number</small>
                                                <div>{request.personalInfo?.phone || "N/A"}</div>
                                            </Col>

                                            {/* Member-specific fields */}
                                            {isMember && (
                                                <>
                                                    {request.personalInfo?.faculty && (
                                                        <Col md={6} className="mb-2">
                                                            <small className="text-muted d-block">Faculty</small>
                                                            <div>{request.personalInfo.faculty}</div>
                                                        </Col>
                                                    )}
                                                    {request.personalInfo?.department && (
                                                        <Col md={6} className="mb-2">
                                                            <small className="text-muted d-block">Department</small>
                                                            <div>{request.personalInfo.department}</div>
                                                        </Col>
                                                    )}
                                                    {request.personalInfo?.salaryStructure && (
                                                        <Col md={12} className="mb-2">
                                                            <small className="text-muted d-block">Salary Structure</small>
                                                            <div>{request.personalInfo.salaryStructure}</div>
                                                        </Col>
                                                    )}
                                                </>
                                            )}

                                            {/* Non-member-specific fields */}
                                            {!isMember && request.personalInfo?.natureOfBusiness && (
                                                <Col md={12} className="mb-2">
                                                    <small className="text-muted d-block">Nature of Business</small>
                                                    <div>{request.personalInfo.natureOfBusiness}</div>
                                                </Col>
                                            )}

                                            {request.personalInfo?.address && (
                                                <Col xs={12} className="mb-2">
                                                    <small className="text-muted d-block">Address</small>
                                                    <div>{request.personalInfo.address}</div>
                                                </Col>
                                            )}

                                            <Col xs={12} className="mb-2 mt-3">
                                                <Row>
                                                    {request.personalInfo?.passport && (
                                                        <Col md={6}>
                                                            <small className="text-muted d-block">Passport Photo</small>
                                                            <div className="mt-2">
                                                                <a href={request.personalInfo.passport} target="_blank" rel="noopener noreferrer">
                                                                    <img
                                                                        src={request.personalInfo.passport}
                                                                        alt="Passport"
                                                                        className="img-thumbnail"
                                                                        style={{ maxHeight: "120px" }}
                                                                    />
                                                                </a>
                                                            </div>
                                                        </Col>
                                                    )}
                                                    {request.personalInfo?.signature && (
                                                        <Col md={6}>
                                                            <small className="text-muted d-block">Signature</small>
                                                            <div className="mt-2">
                                                                <a href={request.personalInfo.signature} target="_blank" rel="noopener noreferrer">
                                                                    <img
                                                                        src={request.personalInfo.signature}
                                                                        alt="Signature"
                                                                        className="img-thumbnail"
                                                                        style={{ maxHeight: "120px" }}
                                                                    />
                                                                </a>
                                                            </div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Tab>

                            {/* Next of Kin Tab - Only for non-members */}
                            {!isMember && request.nextOfKin && (
                                <Tab eventKey="nextofkin" title="Next of Kin">
                                    <Card className="border-0 mt-3">
                                        <Card.Body>
                                            <Row>
                                                <Col md={6} className="mb-2">
                                                    <small className="text-muted d-block">Name</small>
                                                    <div>{request.nextOfKin?.name || "N/A"}</div>
                                                </Col>
                                                <Col md={6} className="mb-2">
                                                    <small className="text-muted d-block">Relationship</small>
                                                    <div>{request.nextOfKin?.relationship || "N/A"}</div>
                                                </Col>
                                                <Col md={6} className="mb-2">
                                                    <small className="text-muted d-block">Phone</small>
                                                    <div>{request.nextOfKin?.phone || "N/A"}</div>
                                                </Col>
                                                <Col md={6} className="mb-2">
                                                    <small className="text-muted d-block">Email</small>
                                                    <div>{request.nextOfKin?.email || "N/A"}</div>
                                                </Col>
                                                <Col xs={12} className="mb-2">
                                                    <small className="text-muted d-block">Address</small>
                                                    <div>{request.nextOfKin?.address || "N/A"}</div>
                                                </Col>
                                            </Row>
                                        </Card.Body>
                                    </Card>
                                </Tab>
                            )}

                            <Tab eventKey="acquisition" title="Acquisition Details">
                                <Card className="border-0 mt-3">
                                    <Card.Body>
                                        <Row className="mb-3">
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Estate Choice</small>
                                                <div className="text-capitalize">
                                                    {request.acquisitionDetails?.estateChoice === 'blossom'
                                                        ? 'Blossom Estate (Iyana Ipaja)'
                                                        : 'University Layout (Iperu)'}
                                                </div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Number of Plots</small>
                                                <div>{request.acquisitionDetails?.numberOfPlots || 0}</div>
                                            </Col>
                                        </Row>

                                        <Card className="bg-light mb-4">
                                            <Card.Body>
                                                <h6 className="card-subtitle mb-2 text-muted">Payment Structure</h6>
                                                <Table borderless responsive className="mb-0">
                                                    <tbody>
                                                        <tr>
                                                            <th>Total Land Cost</th>
                                                            <td className="text-right">
                                                                {formatCurrency(request.acquisitionDetails?.totalAmount || 0)}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Payment Type</th>
                                                            <td className="text-right text-capitalize">
                                                                {request.acquisitionDetails?.paymentType || "N/A"}
                                                                {request.acquisitionDetails?.paymentType === "partial" &&
                                                                    ` (${request.acquisitionDetails?.percentagePaid || 0}%)`}
                                                            </td>
                                                        </tr>
                                                        <tr>
                                                            <th>Amount Paid</th>
                                                            <td className="text-right">
                                                                {formatCurrency(request.acquisitionDetails?.amountPaid || 0)}
                                                            </td>
                                                        </tr>
                                                        {request.acquisitionDetails?.paymentType === "partial" && (
                                                            <>
                                                                <tr>
                                                                    <th>Outstanding Amount</th>
                                                                    <td className="text-right">
                                                                        {formatCurrency(request.acquisitionDetails?.outstandingAmount || 0)}
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Installment Period</th>
                                                                    <td className="text-right">
                                                                        {request.acquisitionDetails?.installmentMonths || 0} months
                                                                    </td>
                                                                </tr>
                                                                <tr>
                                                                    <th>Monthly Installment</th>
                                                                    <td className="text-right">
                                                                        {formatCurrency(request.acquisitionDetails?.monthlyInstallment || 0)}
                                                                    </td>
                                                                </tr>
                                                            </>
                                                        )}
                                                    </tbody>
                                                </Table>
                                            </Card.Body>
                                        </Card>
                                    </Card.Body>
                                </Card>
                            </Tab>

                            <Tab eventKey="payment" title="Payment Information">
                                <Card className="border-0 mt-3">
                                    <Card.Body>
                                        <Row>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Transaction ID</small>
                                                <div>{request.paymentDetails?.transactionId || "N/A"}</div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Teller ID</small>
                                                <div>{request.paymentDetails?.tellerId || "N/A"}</div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Bank Name</small>
                                                <div>{request.paymentDetails?.bankName || "N/A"}</div>
                                            </Col>
                                            <Col md={6} className="mb-2">
                                                <small className="text-muted d-block">Payment Date</small>
                                                <div>
                                                    {request.paymentDetails?.paymentDate
                                                        ? new Date(request.paymentDetails.paymentDate).toLocaleDateString()
                                                        : "N/A"}
                                                </div>
                                            </Col>
                                            {request.paymentDetails?.paymentProof && (
                                                <Col xs={12} className="mt-3">
                                                    <small className="text-muted d-block">Payment Proof</small>
                                                    <div className="mt-2">
                                                        <a href={request.paymentDetails.paymentProof}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm btn-outline-primary">
                                                            View Payment Proof
                                                        </a>
                                                    </div>
                                                </Col>
                                            )}
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Tab>
                        </Tabs>

                        {/* Actions for pending requests */}
                        {isPending && (
                            <div className="mt-4">
                                <Form.Group className="mb-3">
                                    <Form.Label>Rejection Reason (required for rejection)</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="Enter reason for rejection if applicable"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                    />
                                </Form.Group>
                                <div className="d-flex justify-content-end">
                                    <Button
                                        variant="danger"
                                        onClick={handleReject}
                                        disabled={processingAction}
                                        className="mr-2"
                                    >
                                        {processingAction === 'rejected' ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <><XCircle className="mr-1" /> Reject</>
                                        )}
                                    </Button>
                                    <Button
                                        variant="success"
                                        onClick={handleApprove}
                                        disabled={processingAction}
                                    >
                                        {processingAction === 'approved' ? (
                                            <Spinner animation="border" size="sm" />
                                        ) : (
                                            <><CheckCircle className="mr-1" /> Approve</>
                                        )}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Actions for approved requests */}
                        {isApproved && (
                            <div className="d-flex justify-content-end mt-4">
                                <Button
                                    variant="primary"
                                    onClick={handleComplete}
                                    disabled={processingAction}
                                >
                                    {processingAction === 'completed' ? (
                                        <><Spinner animation="border" size="sm" className="mr-2" /> Processing...</>
                                    ) : (
                                        <>Mark as Completed</>
                                    )}
                                </Button>
                            </div>
                        )}
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={processingAction}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default LandAcquisitionDetailsModal;
